<template>
  <QaPageShell :title="agent.agentName || $t('_QAAgentDetail')" :loading="loading" :error="error" :empty="isEmpty" empty-text="ไม่มีเคสของเจ้าหน้าที่คนนี้ในช่วงเวลาที่เลือก" @retry="load">
    <template #toolbar>
      <v-btn variant="text" color="white" size="small" prepend-icon="mdi-arrow-left" @click="$router.push({ name: 'QAAgents' })">{{ $t('qaBack') }}</v-btn>
    </template>

    <template #filters>
      <QaFilterBar @change="load" />
    </template>

    <!-- ชนเพดานต่อคำขอ = ประวัติเคสของคนนี้อาจไม่ครบ ห้ามตัดเงียบ -->
    <v-alert v-if="truncated" type="warning" variant="tonal" density="compact" class="mt-3" icon="mdi-alert-outline">
      <span class="text-body-2">ข้อมูลชนเพดานต่อคำขอ (20,000 เคส) — ตัวเลขของเจ้าหน้าที่คนนี้คิดจากเท่าที่ดึงมาได้ในช่วง {{ windowDays }} วัน ไม่ใช่ทั้งหมด</span>
    </v-alert>

    <!-- สรุปตัวเลขของเจ้าหน้าที่ -->
    <v-card class="qa-card mt-3 pa-4" variant="flat">
      <div class="d-flex align-center flex-wrap ga-4">
        <v-avatar :color="agent.color" size="52">
          <span class="text-h6 text-white">{{ agent.initials }}</span>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="text-h6 font-weight-medium">{{ agent.agentName }}</div>
          <!-- ⚠️ บรรทัดนี้เคยเป็น "ทีม" — ทีมไม่มีแหล่งข้อมูล จึงแสดงบทบาท (Agent | Chatbot)
               ซึ่งเป็นค่าจริงจาก cc.QaAgents.Role แทน · และไม่ upper-case รหัสอีกแล้ว
               เพราะ AgentKey ของจริงเป็นคีย์ที่ต้องก๊อปไปค้นต่อได้ตรงตัว -->
          <div class="text-caption text-medium-emphasis">{{ heroSub }}</div>
        </div>
        <div class="d-flex ga-6 flex-wrap">
          <div v-for="stat in heroStats" :key="stat.label" class="text-center">
            <div class="text-h5 font-weight-medium qa-num" :class="stat.class">{{ stat.value }}</div>
            <div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </v-card>

    <v-row class="mt-1" dense>
      <!-- แนวโน้มคะแนน -->
      <v-col cols="12" md="5">
        <v-card class="qa-card" variant="flat">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaScoreTrend') }}</span>
            <v-spacer />
            <span v-if="agent.trend == null" class="text-caption text-medium-emphasis">ยังเทียบช่วงก่อนไม่ได้</span>
            <span v-else class="text-caption" :class="agent.trend >= 0 ? 'text-success' : 'text-error'">
              {{ agent.trend >= 0 ? '+' : '' }}{{ agent.trend }} เทียบช่วงก่อน
            </span>
          </v-card-title>
          <v-divider />
          <div class="pa-4">
            <apexchart v-if="trendSeries[0].data.length > 1" type="area" height="180" :options="trendOptions" :series="trendSeries" />
            <div v-else class="text-caption text-medium-emphasis text-center py-8">ต้องมีอย่างน้อย 2 เคสจึงจะแสดงแนวโน้มได้</div>
            <div class="text-caption text-medium-emphasis mt-2">แต่ละจุดคือ 1 เคสที่ประเมินแล้ว เรียงจากเก่าไปใหม่</div>
          </div>
        </v-card>
      </v-col>

      <!-- สัดส่วนช่องทาง -->
      <v-col cols="12" md="3">
        <v-card class="qa-card h-100" variant="flat">
          <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaChannelMix') }}</span></v-card-title>
          <v-divider />
          <div class="pa-4">
            <div class="mb-4">
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span>{{ $t('qaVoice') }}</span><span class="qa-num">{{ agent.voiceCount }}</span>
              </div>
              <QaBar :percent="pct(agent.voiceCount)" color="primary" :show-value="false" />
            </div>
            <div>
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span>{{ $t('qaChat') }}</span><span class="qa-num">{{ agent.chatCount }}</span>
              </div>
              <QaBar :percent="pct(agent.chatCount)" color="indigo" :show-value="false" />
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- จุดที่ควรโค้ช -->
      <v-col cols="12" md="4">
        <v-card class="qa-card h-100" variant="flat">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaCoachingFocus') }}</span>
            <v-spacer />
            <span class="text-caption text-medium-emphasis">{{ $t('qaWeakestCriteria') }}</span>
          </v-card-title>
          <v-divider />
          <div class="pa-4">
            <div v-for="c in weakest" :key="c.criteriaId" class="mb-3">
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <!-- scoredCount = จำนวนเคสที่หัวข้อนี้ถูกให้คะแนนจริง (หัวข้อที่โมเดลตอบ N/A
                     ถูกตัดออกจากตัวหารตั้งแต่ Q9) — บอกไว้ใน tooltip เพราะ % จาก 2 เคส
                     กับจาก 200 เคส ไม่ควรถูกเชื่อเท่ากัน -->
                <span class="text-truncate" :title="c.scoredCount ? `คิดจาก ${c.scoredCount} เคสที่ให้คะแนนหัวข้อนี้` : ''">{{ c.name }}</span>
                <span class="qa-num">{{ c.percent }}%</span>
              </div>
              <QaBar :percent="c.percent" :show-value="false" />
            </div>
            <div v-if="!weakest.length" class="text-caption text-medium-emphasis">ยังไม่มีข้อมูลรายหัวข้อ</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ประวัติเคส -->
    <v-card class="qa-card mt-3" variant="flat">
      <v-card-title class="d-flex align-center py-3">
        <span class="text-subtitle-1">{{ $t('qaCaseHistory') }}</span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ recentCases.length }} เคส</span>
      </v-card-title>
      <v-divider />
      <div class="qa-table-wrap pt-3">
        <v-data-table
          :headers="headers"
          :items="recentCases"
          :items-per-page="10"
          class="elevation-0 my-data-table qa-table qa-table-mid"
          density="comfortable"
          fixed-header
          hover
          :row-props="{ class: 'qa-row-click' }"
          @click:row="openCase"
        >
          <template #[`item.startedAt`]="{ item }">
            <span class="qa-num text-body-2">{{ formatDateShort(item.startedAt) }}</span>
          </template>
          <!-- caseType = ประเภทงาน (voice/chat) ของจริงจากคอลัมน์ CaseType ไม่ใช่ช่องทางโซเชียล -->
          <template #[`item.caseType`]="{ item }"><QaChannelChip :channel="item.caseType || 'chat'" /></template>
          <template #[`item.caseReason`]="{ item }"><span>{{ item.caseReason || '—' }}</span></template>
          <template #[`item.durationSec`]="{ item }"><span class="qa-num">{{ formatDuration(item.durationSec) }}</span></template>
          <!-- totalScore = null ได้ (ยังไม่ประเมิน/ข้าม) — QaScoreCell แสดง "—" เอง ห้ามแปลงเป็น 0 -->
          <template #[`item.totalScore`]="{ item }"><QaScoreCell :score="item.totalScore" :status="item.qaStatus" /></template>
          <!-- ⚠️ "OK" ตรงนี้แปลว่า "ไม่ถูกตรวจแล้วไม่พบ" ไม่ใช่ "ตรวจแล้วผ่าน" — gates ยังไม่เปิด
               ทุกแถวจึงเขียว · บอกไว้ใน title เพื่อไม่ให้ถูกอ้างเป็นหลักฐานว่าเคสสะอาด -->
          <template #[`item.isGateFail`]="{ item }">
            <v-chip :color="item.isGateFail ? 'error' : 'success'" size="small" variant="tonal" label :title="item.isGateFail ? '' : $t('qaGatesNotChecked')">
              {{ item.isGateFail ? $t('qaGateFail') : 'OK' }}
            </v-chip>
          </template>
          <!-- ⚠️ ผลตัดสินอยู่ที่ qaStatus · ช่อง status ของ API คือวงจรชีวิตเคส (open/closed)
               ผูกผิดช่องแล้วจะได้ chip "closed" ในคอลัมน์ผลการตัดสินโดยไม่มี error ให้เห็น -->
          <template #[`item.qaStatus`]="{ item }"><QaStatusChip :status="item.qaStatus" /></template>
        </v-data-table>
      </div>
    </v-card>
  </QaPageShell>
</template>

<script>
/**
 * หน้ารายละเอียดเจ้าหน้าที่ 1 คน
 *
 * Q10 — hero / แนวโน้ม / จุดที่ควรโค้ช / ประวัติเคส มาจาก `analytics/agents/{agentKey}`
 * ของจริงทั้งหมด · `agentId` ที่ส่งมากับ query คือ **AgentKey จริง** ที่หน้า Overview/Agents
 * ส่งต่อมา (เดิมเป็นคีย์ของชุดข้อมูลจำลอง — ถ้าปล่อยหน้านี้ไว้บน mock หน้าจะพังทันที
 * ที่สองหน้านั้นเป็นของจริง)
 *
 * ⚠️ แถวใน recentCases เป็นรูปเดียวกับหน้า Case Explorer (QaCaseListItem ของ server)
 *    ไม่ใช่รูปที่ mock เคยส่ง — ชื่อช่องจึงเป็น startedAt / caseType / caseReason / qaStatus
 */
import VueApexCharts from 'vue3-apexcharts';
import qaConsole from '@/services/qaConsole';
import { formatDateShort, formatDuration } from '@/utils/qaFormat';
import QaPageShell from '@/components/QAConsole/QaPageShell.vue';
import QaFilterBar from '@/components/QAConsole/QaFilterBar.vue';
import QaScoreCell from '@/components/QAConsole/QaScoreCell.vue';
import QaStatusChip from '@/components/QAConsole/QaStatusChip.vue';
import QaChannelChip from '@/components/QAConsole/QaChannelChip.vue';
import QaBar from '@/components/QAConsole/QaBar.vue';

export default {
  name: 'QAAgentDetail',
  // apexchart ลงทะเบียนแบบ local ตาม pattern เดิมของโปรเจกต์ (ดู components/TaxDashboard/*)
  components: { apexchart: VueApexCharts, QaPageShell, QaFilterBar, QaScoreCell, QaStatusChip, QaChannelChip, QaBar },
  data() {
    return {
      loading: true,
      error: null,
      agent: {},
      scoreTrend: [],
      criteriaAverages: [],
      recentCases: [],
      truncated: false,
      windowDays: 0,
    };
  },
  computed: {
    agentId() {
      return this.$route.query.agentId;
    },
    isEmpty() {
      return !this.loading && !this.error && !this.recentCases.length;
    },
    heroSub() {
      // บทบาทว่างได้ (เจ้าหน้าที่ที่ยังไม่มีแถวใน cc.QaAgents) — อย่าให้เหลือ " · " ลอย ๆ
      return [this.agent.role, this.agent.agentId ? `รหัส ${this.agent.agentId}` : ''].filter(Boolean).join(' · ');
    },
    weakest() {
      return this.criteriaAverages.slice(0, 4);
    },
    heroStats() {
      return [
        // ⚠️ null = ยังไม่มีเคสที่ประเมินสำเร็จ · ต้องขึ้น "—" ไม่ใช่ 0 (ดูคอมเมนต์ใน QaScoreCell)
        { label: this.$t('qaAvgScore'), value: this.agent.avgScore ?? '—', class: '' },
        { label: this.$t('qaPassRate'), value: this.agent.passRate == null ? '—' : `${this.agent.passRate}%`, class: '' },
        // ⚠️ "เคสที่ประเมินแล้ว" ไม่ใช่ "เคส" — caseCount นับเฉพาะเคสที่ประเมินสำเร็จ
        //    ที่เหลืออยู่ในช่องถัดไป ไม่งั้นจะอ่านเป็นภาระงานทั้งหมดของคนนี้
        { label: this.$t('qaCasesEvaluated'), value: this.agent.caseCount ?? 0, class: '' },
        { label: this.$t('qaNotEvaluatedFilter'), value: this.agent.notEvaluated ?? 0, class: 'text-medium-emphasis' },
        { label: this.$t('qaFails'), value: this.agent.failCount ?? 0, class: this.agent.failCount ? 'text-error' : 'text-success' },
      ];
    },
    trendSeries() {
      return [{ name: this.$t('qaQaScore'), data: this.scoreTrend }];
    },
    trendOptions() {
      return {
        chart: { toolbar: { show: false }, sparkline: { enabled: false }, fontFamily: 'inherit' },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['#1867C0'],
        fill: { type: 'gradient', gradient: { shadeIntensity: 0.2, opacityFrom: 0.35, opacityTo: 0.05 } },
        dataLabels: { enabled: false },
        xaxis: { labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } },
        yaxis: { min: 0, max: 100, tickAmount: 4, labels: { style: { fontSize: '11px' } } },
        grid: { borderColor: '#eef0f2', strokeDashArray: 4 },
        tooltip: { y: { formatter: (v) => `${v} / 100` } },
      };
    },
    headers() {
      return [
        { title: this.$t('qaDate'), key: 'startedAt', width: '100px' },
        { title: this.$t('qaType'), key: 'caseType', sortable: false, width: '110px' },
        { title: this.$t('qaCaseReason'), key: 'caseReason', minWidth: '200px' },
        { title: this.$t('qaDuration'), key: 'durationSec', align: 'end', width: '100px' },
        { title: this.$t('qaQaScore'), key: 'totalScore', align: 'end', width: '110px' },
        { title: this.$t('qaCompliance'), key: 'isGateFail', sortable: false, width: '120px' },
        { title: this.$t('qaResult'), key: 'qaStatus', sortable: false, width: '120px' },
      ];
    },
  },
  watch: {
    agentId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDateShort,
    formatDuration,
    pct(n) {
      const total = (this.agent.voiceCount || 0) + (this.agent.chatCount || 0);
      return total ? Math.round((n / total) * 100) : 0;
    },
    async load() {
      if (!this.agentId) {
        this.$router.replace({ name: 'QAAgents' });
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const res = await qaConsole.getAgentDetail(this.agentId, this.$store.getters['qaConsole/filter']);
        // agent ที่ไม่มีเคสในช่วงที่เลือกได้ผลว่าง ไม่ใช่ 404 — ช่องที่ขาดต้องกลายเป็น
        // ค่าว่างที่ template อ่านได้ ไม่ใช่ null ที่ทำให้ hero ล้มทั้งหน้า
        this.agent = res.agent || {};
        this.scoreTrend = res.scoreTrend || [];
        this.criteriaAverages = res.criteriaAverages || [];
        this.recentCases = res.recentCases || [];
        this.truncated = !!res.truncated;
        this.windowDays = res.windowDays;
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
    openCase(_event, { item }) {
      this.$router.push({ name: 'QACaseDetail', query: { caseNo: item.caseNo, from: 'QAAgentDetail', agentId: this.agentId } });
    },
  },
};
</script>
